import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { Notice } from "../components/ui";
import type { Challenge } from "./client";
export default function AltchaVerification({
  challenge,
  onSolved,
}: {
  challenge: Challenge;
  onSolved: (pass: string) => void;
}) {
  const { t } = useI18n();
  const worker = useRef<Worker | null>(null);
  const request = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false),
    [done, setDone] = useState(false);
  useEffect(
    () => () => {
      worker.current?.terminate();
      request.current?.abort();
    },
    [],
  );
  const solve = () => {
    setBusy(true);
    setFailed(false);
    const controller = new AbortController();
    request.current = controller;
    const w = new Worker(new URL("./altcha.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.current = w;
    w.onerror = () => {
      w.terminate();
      setBusy(false);
      setFailed(true);
    };
    w.onmessage = async (e) => {
      w.terminate();
      try {
        if (e.data.error) throw new Error();
        const res = await fetch("/api/v1/security/challenge/verify", {
          method: "POST",
          credentials: "same-origin",
          signal: controller.signal,
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ id: challenge.id, response: e.data.response }),
        });
        const data = await res.json();
        if (!res.ok || typeof data.pass !== "string") throw new Error();
        if (!controller.signal.aborted) {
          setDone(true);
          onSolved(data.pass);
        }
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    };
    w.postMessage(challenge);
  };
  return (
    <div className="stack">
      {failed && <Notice tone="error">{t("shield.verifyFailed")}</Notice>}
      <button
        type="button"
        className="button"
        disabled={busy || done || failed}
        onClick={solve}
      >
        {t(
          done
            ? "shield.cap.solved"
            : busy
              ? "shield.verifying"
              : "shield.verifyStart",
        )}
      </button>
    </div>
  );
}
